/**
 * Microsoft Graph API utility with comprehensive throttling:
 * - Request queuing to spread requests over time
 * - Exponential backoff with Retry-After header/body parsing
 * - Rate limiting (1-2 req/sec for Teams, slower for others)
 * - Request caching to reduce redundant calls
 * - $select optimization support
 */

// Global request queue to manage concurrent requests and rate limiting
const requestQueue = {
  queue: [],
  running: 0,
  maxConcurrent: 2, // Limit concurrent requests
  minDelay: 500, // Minimum 500ms between requests (2 req/sec max)
  lastRequestTime: 0,
  // Separate queue for Teams chat messages (1 req/sec)
  teamsQueue: [],
  teamsRunning: 0,
  teamsLastRequestTime: 0,
};

// Request cache to prevent duplicate calls
const requestCache = new Map();
const CACHE_TTL = 10000; // 10 seconds cache
const MAX_CACHE_SIZE = 100;

// Generate cache key from URL and options
function getCacheKey(url, options = {}) {
  const method = options.method || 'GET';
  const body = options.body ? JSON.stringify(options.body) : '';
  return `${method}:${url}:${body}`;
}

// Clean up old cache entries
function cleanupCache() {
  if (requestCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(requestCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => requestCache.delete(key));
  }
}

// Process main request queue
async function processQueue() {
  if (requestQueue.running >= requestQueue.maxConcurrent || requestQueue.queue.length === 0) {
    return;
  }

  const now = Date.now();
  const timeSinceLastRequest = now - requestQueue.lastRequestTime;
  const delay = Math.max(0, requestQueue.minDelay - timeSinceLastRequest);

  if (delay > 0) {
    setTimeout(() => processQueue(), delay);
    return;
  }

  const item = requestQueue.queue.shift();
  if (!item) return;

  requestQueue.running++;
  requestQueue.lastRequestTime = Date.now();

  try {
    const result = await item.fn();
    item.resolve(result);
  } catch (error) {
    item.reject(error);
  } finally {
    requestQueue.running--;
    processQueue();
  }
}

// Process Teams-specific queue (1 req/sec)
async function processTeamsQueue() {
  if (requestQueue.teamsRunning >= 1 || requestQueue.teamsQueue.length === 0) {
    return;
  }

  const now = Date.now();
  const timeSinceLastRequest = now - requestQueue.teamsLastRequestTime;
  const delay = Math.max(0, 1000 - timeSinceLastRequest); // 1 second between Teams requests

  if (delay > 0) {
    setTimeout(() => processTeamsQueue(), delay);
    return;
  }

  const item = requestQueue.teamsQueue.shift();
  if (!item) return;

  requestQueue.teamsRunning++;
  requestQueue.teamsLastRequestTime = Date.now();

  try {
    const result = await item.fn();
    item.resolve(result);
  } catch (error) {
    item.reject(error);
  } finally {
    requestQueue.teamsRunning--;
    processTeamsQueue();
  }
}

// Add request to main queue
function queueRequest(fn) {
  return new Promise((resolve, reject) => {
    requestQueue.queue.push({ fn, resolve, reject });
    processQueue();
  });
}

// Add request to Teams queue (slower rate)
function queueTeamsRequest(fn) {
  return new Promise((resolve, reject) => {
    requestQueue.teamsQueue.push({ fn, resolve, reject });
    processTeamsQueue();
  });
}

/**
 * Parse retry time from 429/503 response
 * Checks both Retry-After header and retryAfterSeconds in JSON body
 */
async function parseRetryTime(response) {
  // First, try to get retryAfterSeconds from JSON error body
  try {
    const errorBody = await response.clone().json().catch(() => null);
    if (errorBody?.error?.retryAfterSeconds) {
      return errorBody.error.retryAfterSeconds * 1000;
    }
  } catch (e) {
    // JSON parsing failed, continue to header check
  }

  // Check Retry-After header
  const retryAfter = response.headers.get("Retry-After");
  if (retryAfter) {
    // Retry-After can be seconds (number) or HTTP-date
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000;
    }
    // Try parsing as HTTP-date
    const date = new Date(retryAfter);
    if (!isNaN(date.getTime())) {
      return Math.max(1000, date.getTime() - Date.now());
    }
  }

  return null; // No retry time specified
}

/**
 * Enhanced fetch with retry logic, throttling, and caching
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} retries - Maximum number of retries (default: 5)
 * @param {boolean} useCache - Whether to use cache (default: true for GET requests)
 * @param {boolean} isTeamsRequest - Whether this is a Teams API request (slower rate)
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(
  url,
  options = {},
  retries = 5,
  useCache = true,
  isTeamsRequest = false
) {
  // Check cache for GET requests
  if (useCache && (!options.method || options.method === 'GET')) {
    const cacheKey = getCacheKey(url, options);
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      // Return a new Response-like object from cached data
      return new Response(JSON.stringify(cached.data), {
        status: cached.status,
        statusText: cached.statusText,
        headers: cached.headers,
      });
    }
  }

  // Queue the request to control concurrency and rate
  const queueFn = isTeamsRequest ? queueTeamsRequest : queueRequest;
  const response = await queueFn(async () => {
    const res = await fetch(url, options);

    // Handle throttling (429 or 503)
    if ((res.status === 429 || res.status === 503) && retries > 0) {
      // Parse retry time from header or body
      const retryTime = await parseRetryTime(res);
      
      let waitTime;
      if (retryTime) {
        // Use the exact retry time from API, with a small buffer
        waitTime = Math.max(retryTime, 1000);
      } else {
        // Exponential backoff: 2s, 4s, 8s, 16s, 32s
        const attempt = 5 - retries;
        waitTime = Math.min(32000, 2000 * Math.pow(2, attempt));
      }

      console.warn(
        `Graph API throttled (${res.status}), waiting ${Math.round(waitTime / 1000)}s before retry (${retries} retries left)`
      );
      await new Promise((r) => setTimeout(r, waitTime));
      return fetchWithRetry(url, options, retries - 1, useCache, isTeamsRequest);
    }

    // If retries exhausted and still throttled, return null gracefully
    if ((res.status === 429 || res.status === 503) && retries === 0) {
      console.error(`Graph API throttled (${res.status}), all retries exhausted`);
      return null;
    }

    // Cache successful GET responses (skip caching for blob/binary responses)
    if (useCache && res.ok && (!options.method || options.method === 'GET')) {
      const contentType = res.headers.get('content-type') || '';
      // Don't cache binary/blob responses (images, files, etc.)
      if (!contentType.includes('image/') && !contentType.includes('application/octet-stream')) {
        try {
          const data = await res.clone().json().catch(() => res.clone().text());
          const cacheKey = getCacheKey(url, options);
          requestCache.set(cacheKey, {
            data,
            status: res.status,
            statusText: res.statusText,
            headers: Object.fromEntries(res.headers.entries()),
            timestamp: Date.now(),
          });
          cleanupCache();
        } catch (e) {
          // Ignore cache errors
        }
      }
    }

    if (!res.ok && res.status !== 404) {
      throw new Error(`Graph HTTP ${res.status}: ${res.statusText}`);
    }

    return res;
  });

  return response;
}

/**
 * Debounce function to prevent rapid successive calls
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(fn, delay = 500) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Clear the request cache (useful for testing or forced refresh)
 */
export function clearCache() {
  requestCache.clear();
}

/**
 * Get current queue status (for debugging)
 */
export function getQueueStatus() {
  return {
    queueLength: requestQueue.queue.length,
    running: requestQueue.running,
    teamsQueueLength: requestQueue.teamsQueue.length,
    teamsRunning: requestQueue.teamsRunning,
    cacheSize: requestCache.size,
  };
}

